import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { Box, Chip, ImageListItem, Paper, Skeleton, Stack, Typography } from "@mui/material";
import type { FC, MouseEvent } from "react";
import { useEffect, useState } from "react";
import type { ImageFlavour } from "../../publicPackages/publicPackagesApi";
import { storageUrlWithCredentials } from "./storageUrlWithCredentials";

export interface Image {
  readonly id: string;
  readonly name: string;
  readonly tags: string[];
  readonly isPrivate: boolean;
  readonly info?: string;
  readonly source?: string;
  readonly thumbnail: {
    readonly url: string;
  };
  readonly original: {
    readonly url: string;
  };
  readonly www: {
    readonly url: string;
  };
  readonly flavours: Partial<Record<ImageFlavour, { readonly url: string }>>;
}

interface Props {
  readonly image: Image;
  readonly width: number;
  readonly height?: number;
  readonly selectMode?: boolean;
  readonly selected?: boolean;
  readonly onToggleSelection?: () => void;
  readonly onImageClick: () => void;
}

const PrettyImagePreview: FC<Props> = ({
  image,
  width,
  selectMode,
  selected = false,
  onToggleSelection,
  onImageClick,
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  const displayName = image.name.replace(/\.(jpe?g|png|webp|gif|bmp|svg)$/i, "");

  const handleToggle = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (onToggleSelection) {
      onToggleSelection();
    }
  };

  const handleCardClick = () => {
    if (selectMode) {
      if (onToggleSelection) {
        onToggleSelection();
      }
      return;
    }
    onImageClick();
  };

  useEffect(() => {
    storageUrlWithCredentials(image.thumbnail.url).then((url) => setThumbnailUrl(url ?? null));
  }, [image.thumbnail.url]);

  return (
    <ImageListItem sx={{ width: "100%", height: "auto", p: 0 }}>
      <Paper
        elevation={0}
        onClick={handleCardClick}
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          border: selected ? "2px solid #3557a1" : "1px solid rgba(0,0,0,0.08)",
          cursor: "pointer",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease",
          boxShadow: selected ? "0 8px 24px rgba(53, 87, 161, 0.18)" : "none",
          width: "100%",
          maxWidth: width,
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          },
        }}
      >
        <Box sx={{ position: "relative", aspectRatio: "4 / 3", bgcolor: "rgba(0,0,0,0.04)" }}>
          {thumbnailUrl ? (
            <Box
              component="img"
              src={thumbnailUrl}
              alt={image.name}
              loading="lazy"
              draggable={false}
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }}
            />
          ) : (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          )}

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: selected
                ? "linear-gradient(180deg, rgba(53,87,161,0.12) 0%, rgba(0,0,0,0.35) 100%)"
                : "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.55) 100%)",
              pointerEvents: "none",
            }}
          />

          <Box
            role="checkbox"
            aria-checked={selected}
            aria-label={`Zaznacz ${image.name}`}
            tabIndex={0}
            onClick={handleToggle}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleToggle(event as unknown as MouseEvent<HTMLElement>);
              }
            }}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 56,
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 3,
              "&:hover .media-check-icon": {
                transform: "scale(1.08)",
              },
            }}
          >
            <Box
              className="media-check-icon"
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.15s ease",
                border: selected ? "none" : "2px solid rgba(255,255,255,0.95)",
                bgcolor: selected ? "#3557a1" : "rgba(0,0,0,0.3)",
                boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
              }}
            >
              {selected ? <CheckCircleIcon sx={{ fontSize: 18, color: "#fff" }} /> : null}
            </Box>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              position: "absolute",
              left: 8,
              right: 8,
              bottom: 8,
              gap: 0.5,
              flexWrap: "wrap",
              pointerEvents: "none",
            }}
          >
            {image.tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  bgcolor: "rgba(255,255,255,0.92)",
                  color: "text.primary",
                  border: "1px solid rgba(0,0,0,0.06)",
                }}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ p: 1.75, pb: 3.25 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.3, mb: 0.5, fontSize: "0.8125rem", fontWeight: 600 }}>
            {displayName}
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ lineHeight: 1.35, display: "block", fontSize: "0.7rem" }}
          >
            {image.info || image.source || "Zdjęcie"}
          </Typography>
        </Box>
      </Paper>
    </ImageListItem>
  );
};

export default PrettyImagePreview;
